import React from 'react';
import { Check, CircleAlert, CircleCheck, LoaderCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import { PASSWORD_MIN_LENGTH } from '../../policy/passwordPolicy';
import {
  INCLUDE_RULES,
  MAX_STRENGTH_LEVEL,
  issueMessage,
  type PasswordStatus,
} from '../../passwordStatus';
import type { NewPasswordState } from '../../hooks/useNewPassword';
import './PasswordRequirements.css';

/** The bars of the strength meter, one per level. */
const STRENGTH_BARS = Array.from({ length: MAX_STRENGTH_LEVEL }, (_, index) => index + 1);

type ChipState = 'is-met' | 'is-error' | 'is-pending';

/** What is missing only turns red once the user has left the field. */
function chipStateOf(ok: boolean, touched: boolean, empty: boolean): ChipState {
  if (ok) return 'is-met';
  return touched && !empty ? 'is-error' : 'is-pending';
}

const CHIP_ICON: Record<ChipState, React.ReactNode> = {
  'is-met': <Check size={12} aria-hidden />,
  'is-error': <X size={12} aria-hidden />,
  'is-pending': null,
};

/** The single line under the pills: what broke it, the lookup, the all-clear,
 *  or what to avoid. */
function StatusMessage({ status }: { status: PasswordStatus }) {
  const { t } = useTranslation(NS);
  if (status.issue) {
    const { key, values } = issueMessage(status.issue);
    return (
      <p className="pwreq__msg is-error">
        <CircleAlert size={14} aria-hidden />
        {t(key, values)}
      </p>
    );
  }
  if (status.checking) {
    return (
      <p className="pwreq__msg is-checking">
        <LoaderCircle size={14} aria-hidden className="pwreq__spin" />
        {t('status.checking')}
      </p>
    );
  }
  if (status.done) {
    return (
      <p className="pwreq__msg is-ok">
        <CircleCheck size={14} aria-hidden />
        {t('status.done')}
      </p>
    );
  }
  return <p className="pwreq__msg">{t('avoidHint')}</p>;
}

/**
 * How the password is doing, under the field (Design, `PasswordRequirements`).
 * It teaches only what helps at each moment instead of eight fixed rules:
 *
 * - Before typing: one line with what to use and what to avoid.
 * - While typing: a strength bar and five pills with what to include (8+, A–Z,
 *   a–z, 0–9, symbol) that turn green. What is missing is not an error while
 *   typing; after leaving the field, it is.
 * - If something breaks it (personal data, a sequence, the blocklist, a breach,
 *   the API): a single message with what happened and what to do.
 * - When everything passes: the pills fold away and the confirmation stays.
 *
 * Announcements for screen readers live in `NewPasswordFields`, outside the
 * element that describes the field, so they are not read twice.
 */
export function PasswordRequirements({ id, state }: { id: string; state: NewPasswordState }) {
  const { t } = useTranslation(NS);
  const { check, focused, touched, status } = state;
  const { empty, locallyValid, issue, level } = status;
  const min = PASSWORD_MIN_LENGTH;

  if (empty && !focused) {
    return (
      <p id={id} className="pwreq__hint">
        {t('hint', { min })}
      </p>
    );
  }

  return (
    <div id={id} className="pwreq">
      {!empty && (
        <div className={`pwreq__meter is-level-${level}`}>
          <div className="pwreq__bars" aria-hidden>
            {STRENGTH_BARS.map((bar) => (
              <span key={bar} className={bar <= level ? 'is-on' : undefined} />
            ))}
          </div>
          <span className="pwreq__level">
            <span className="pwreq__sr">{t('strength.label')}</span>
            {t(`strength.${level}`)}
          </span>
        </div>
      )}

      {!locallyValid && !issue && (
        <ul className="pwreq__chips" aria-label={t('rules.title')}>
          {INCLUDE_RULES.map((rule) => {
            const { ok } = check[rule.id];
            const chip = chipStateOf(ok, touched, empty);
            return (
              <li key={rule.id} className={`pwreq__chip ${chip}`}>
                {CHIP_ICON[chip]}
                <span aria-hidden>{t(`rules.${rule.id}.short`, { min })}</span>
                <span className="pwreq__sr">
                  {t(ok ? 'rules.met' : 'rules.notMet', {
                    rule: t(`rules.${rule.id}.label`, { min }),
                  })}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <StatusMessage status={status} />
    </div>
  );
}
